// Two-button download bar for the regulator-ready PDF + DOCX exports.
// Each library is lazy-loaded on first click so the initial bundle stays lean.
import { useState } from 'react';

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Slightly defer revoke so Safari/Firefox can finish initiating download.
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function DownloadButtons({ result, extracted }) {
  const [busy, setBusy] = useState(null); // 'pdf' | 'docx' | null
  const [error, setError] = useState(null);

  async function downloadPdf() {
    setError(null);
    setBusy('pdf');
    try {
      const { buildReportPdf } = await import('../lib/pdfExport.js');
      const { blob, filename } = await buildReportPdf({ result, extracted });
      triggerDownload(blob, filename);
    } catch (err) {
      setError(`PDF export failed: ${err?.message || err}`);
    } finally {
      setBusy(null);
    }
  }

  async function downloadDocx() {
    setError(null);
    setBusy('docx');
    try {
      const { buildReportDocx } = await import('../lib/docxExport.js');
      const { blob, filename } = await buildReportDocx({ result, extracted });
      triggerDownload(blob, filename);
    } catch (err) {
      setError(`DOCX export failed: ${err?.message || err}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={downloadPdf}
          disabled={busy !== null}
          className="rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy === 'pdf' ? 'Building PDF…' : 'Download PDF'}
        </button>
        <button
          type="button"
          onClick={downloadDocx}
          disabled={busy !== null}
          className="rounded-md border border-gold px-4 py-2 text-sm font-semibold text-gold transition hover:bg-gold hover:text-navy-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy === 'docx' ? 'Building Word doc…' : 'Download Word (.docx)'}
        </button>
      </div>
      {error && (
        <div className="rounded-md border border-red-900/50 bg-red-900/20 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}
